import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client";
import { DateRangePicker, defaultRange } from "../components/DateRangePicker";
import { QueryError } from "../components/QueryError";
import { isRejectionsSummary, type RejectionsSummary } from "./dashboardStats";

/**
 * The triage view in front of the Rejections list: what went wrong, how much
 * of it, and what to fix first. Two kinds of rejection, deliberately shown
 * apart because they mean different things to the customer:
 *
 *   FINTRAC  -- we built a report and the regulator refused it. Nothing is
 *               filed and the deadline is still running. Unit: reports.
 *   Ours     -- the data never entered the pipeline (API or SFTP). Unit: rows.
 *
 * A third, smaller line covers reports that failed inside our own pipeline:
 * Scadox's to fix, so a customer sees only that they exist.
 *
 * Every number is raw for the selected range -- nothing here knows whether a
 * rejection was later fixed and re-sent -- so the page says "in this range"
 * rather than implying an open backlog.
 */

interface Props {
  clientId?: string;
  initialRange?: { from: string; to: string };
  /** Internal users get the pipeline-error breakdown (the API only sends it
   *  to them); customers get the count. */
  internal?: boolean;
}

function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

function Deadline({ next, overdue }: { next: string | null; overdue: number }) {
  return (
    <>
      {next ?? "—"}
      {overdue > 0 && <span className="pill bad" style={{ marginLeft: 6 }}>{overdue} past</span>}
    </>
  );
}

export function RejectionsSummaryPage({ clientId, initialRange, internal }: Props = {}) {
  const navigate = useNavigate();
  const [{ from, to }, setRange] = useState(initialRange ?? defaultRange());
  const scope = clientId ? `&client_id=${encodeURIComponent(clientId)}` : "";
  // Absolute for the same reason as DashboardPage's links.
  const base = clientId ? `/customers/${encodeURIComponent(clientId)}` : "";

  const { data, isLoading, error } = useQuery({
    queryKey: ["rejections-summary", from, to, clientId ?? null],
    queryFn: () => apiFetch<RejectionsSummary>(
      `/portal/rejections/summary?from=${from}&to=${to}${scope}`),
    enabled: Boolean(from && to),
  });

  const open = (filters: Record<string, string>) =>
    navigate(`${base}/rejections/list?${new URLSearchParams({ from, to, ...filters })}`);

  const ok = isRejectionsSummary(data);
  const f = ok ? data.fintrac : undefined;
  const p = ok ? data.pipeline : undefined;
  const api = ok ? data.validation.api : undefined;
  const sftp = ok ? data.validation.sftp : undefined;
  const nothing = ok && f!.report_count === 0 && p!.report_count === 0
    && api!.row_count === 0 && sftp!.row_count === 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <DateRangePicker from={from} to={to} onChange={(a, b) => setRange({ from: a, to: b })} />
        <button type="button" className="link-btn" onClick={() => open({})}>
          View every rejection →
        </button>
      </div>
      {isLoading && <p>Loading…</p>}
      {error && <QueryError error={error as Error} what="rejections summary" />}
      {nothing && <p className="empty">No rejections in this range.</p>}

      {ok && !nothing && (
        <>
          <section className="card rej-section bad">
            <h3>Rejected by FINTRAC — {plural(f!.report_count, "report")} not filed</h3>
            <p className="lede">
              FINTRAC refused these reports, so they are not on file and their deadlines are
              still running. Most need data corrected and re-sent.
            </p>
            {f!.groups.length === 0 ? (
              <p className="empty">None in this range.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>FINTRAC rule</th><th>Reports</th><th>Report types</th><th>Nearest deadline</th></tr>
                </thead>
                <tbody>
                  {f!.groups.map((g) => (
                    <tr key={g.rule ?? "none"} className="clickable"
                        onClick={() => open({ f_source: "fintrac", f_kind: "fintrac_rejected",
                                              ...(g.rule ? { f_reason: g.rule } : {}) })}>
                      <td className="mono">{g.rule ?? "No rule code"}</td>
                      <td>{g.report_count}</td>
                      <td>{g.report_types.join(", ")}</td>
                      <td><Deadline next={g.next_deadline} overdue={g.overdue_count} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {f!.groups.length > 1 && (
              <p className="rej-note">A report that broke several rules is counted under each of them.</p>
            )}
          </section>

          <section className="card rej-section warn">
            <h3>Rejected at ingestion — {plural(api!.row_count + sftp!.row_count, "row")}</h3>
            <p className="lede">
              These rows failed our validation and never entered the pipeline, so nothing was
              reported from them. Fix the field and send them again.
            </p>
            {api!.groups.length + sftp!.groups.length === 0 ? (
              <p className="empty">None in this range.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Channel</th><th>Field</th><th>Error</th><th>Count</th></tr>
                </thead>
                <tbody>
                  {api!.groups.map((g) => (
                    <tr key={`api:${g.field}:${g.code}`} className="clickable"
                        onClick={() => open({ f_source: "validation",
                                              f_reason: g.field ?? g.code ?? "" })}>
                      <td><span className="pill warn">API</span></td>
                      <td className="mono">{g.field ?? "—"}</td>
                      <td className="mono">{g.code ?? "—"}</td>
                      <td>{plural(g.count, "row")}</td>
                    </tr>
                  ))}
                  {sftp!.groups.map((g) => (
                    <tr key={`sftp:${g.code}`} className="clickable"
                        onClick={() => open({ f_source: "sftp", f_reason: g.code })}>
                      <td><span className="pill mute">SFTP</span></td>
                      {/* SFTP audit summaries record a code->count map per
                          file, with no field name -- not "unknown field". */}
                      <td style={{ color: "var(--dim)" }}>per file</td>
                      <td className="mono">{g.code}</td>
                      <td>{plural(g.count, "error")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {sftp!.file_count > 0 && (
              <p className="rej-note">
                SFTP: {plural(sftp!.row_count, "row")} rejected across {plural(sftp!.file_count, "file")}.
                A row can carry more than one error.
              </p>
            )}
          </section>

          {p!.report_count > 0 && (
            <section className="card rej-section">
              <h3>Delayed on our side — {plural(p!.report_count, "report")}</h3>
              <p className="lede">
                These failed inside Scadox before they could be filed. Nothing is needed from you;
                Scadox has been notified.
                {p!.next_deadline && <> Nearest deadline {p!.next_deadline}.</>}
              </p>
              {internal && p!.groups && p!.groups.length > 0 && (
                <table className="data-table">
                  <thead><tr><th>Failure</th><th>Reports</th></tr></thead>
                  <tbody>
                    {p!.groups.map((g) => (
                      <tr key={g.signature}>
                        <td className="mono">{g.signature}</td>
                        <td>{g.report_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <button type="button" className="link-btn"
                      onClick={() => open({ f_source: "fintrac", f_kind: "pipeline_error" })}>
                View these reports →
              </button>
            </section>
          )}

          {data!.truncated && (
            <p className="rej-note">
              Too many failed reports to group them all; the FINTRAC counts above are a minimum.
              Narrow the date range for exact figures.
            </p>
          )}
        </>
      )}
    </div>
  );
}
